import { SnackbarKey, useSnackbar } from 'notistack';
import { ReactNode, createContext, useCallback, useContext, useMemo, useRef } from 'react';

export interface WaiverToasts {
    /** Record the toast raised for an account, replacing and closing whatever held that slot. */
    readonly track: (accountKey: string, snackbarKey: SnackbarKey) => void;
    /** Take down the toast raised for one account, if one is still up. */
    readonly dismiss: (accountKey: string) => void;
    /** The toast currently standing for an account, so a stale one can tell it is not the live one. */
    readonly keyFor: (accountKey: string) => SnackbarKey | undefined;
    /** Take every waiver toast down at once. The unmount path — no latch. */
    readonly dismissAll: () => void;
    /**
     * Take every toast down *and* refuse new ones. What the step calls when completion starts.
     *
     * A latch rather than a one-shot dismiss, because a waiver write can already be in flight when
     * the member clicks Continue: its `dismiss` ran before the write, `dismissAll` then finds
     * nothing tracked for that account, and the write lands afterwards with the section still
     * mounted — raising a fresh toast, with a live Undo, over the completion it arrived too late
     * to be part of.
     */
    readonly suspend: () => void;
    /** Allow toasts again. Completion failed and the member is still on the step. */
    readonly resume: () => void;
    /** Whether toasts are currently refused. Read before raising one, next to the mounted check. */
    readonly isSuspended: () => boolean;
}

const WaiverToastsContext = createContext<WaiverToasts | undefined>(undefined);

/**
 * The waiver toasts currently on screen, owned above the section that raises them.
 *
 * `SnackbarProvider` is mounted at the app root, so a waiver toast outlives the section. The
 * section's own unmount is too late a hook to hang the teardown on: `completeStepAndExit` fires
 * both `completeStep` round trips while the section is still mounted, and notistack's container
 * (z-index 1400) clears MUI Dialog's (1300), so the toast stays clickable over the
 * incomplete-statements dialog as well. An Undo clicked anywhere in that stretch posts
 * `persistStepData` with its hardcoded `status: 'in_progress'`, reopening a step whose
 * transaction-extraction fan-out has already run.
 *
 * Holding the registry at the step instead means the step can retire them the moment completion
 * *starts*, which is the whole window rather than the tail of it.
 */
export function useWaiverToastRegistry(): WaiverToasts {
    const { closeSnackbar } = useSnackbar();
    // Identity is fixed for the life of the step, so the callbacks below stay stable and the
    // section's unmount cleanup closes over the same map its handlers wrote to.
    const openToasts = useRef<Map<string, SnackbarKey>>(new Map());
    /** Set while the step is completing, so a write that lands late cannot raise a toast. */
    const isSuspended = useRef(false);

    const dismiss = useCallback(
        (accountKey: string) => {
            const snackbarKey = openToasts.current.get(accountKey);
            if (snackbarKey === undefined) {
                return;
            }
            openToasts.current.delete(accountKey);
            closeSnackbar(snackbarKey);
        },
        [closeSnackbar]
    );

    const track = useCallback(
        (accountKey: string, snackbarKey: SnackbarKey) => {
            // Dismiss first: two waives of one account can overlap, and the loser would otherwise
            // be overwritten rather than closed — untracked, so nothing can take it down.
            dismiss(accountKey);
            openToasts.current.set(accountKey, snackbarKey);
        },
        [dismiss]
    );

    const keyFor = useCallback((accountKey: string) => openToasts.current.get(accountKey), []);

    const dismissAll = useCallback(() => {
        openToasts.current.forEach((snackbarKey) => closeSnackbar(snackbarKey));
        openToasts.current.clear();
    }, [closeSnackbar]);

    const suspend = useCallback(() => {
        isSuspended.current = true;
        dismissAll();
    }, [dismissAll]);

    const resume = useCallback(() => {
        isSuspended.current = false;
    }, []);

    const readIsSuspended = useCallback(() => isSuspended.current, []);

    return useMemo(
        () => ({ track, dismiss, keyFor, dismissAll, suspend, resume, isSuspended: readIsSuspended }),
        [track, dismiss, keyFor, dismissAll, suspend, resume, readIsSuspended]
    );
}

export function WaiverToastsProvider({ value, children }: Readonly<{ value: WaiverToasts; children: ReactNode }>) {
    return <WaiverToastsContext.Provider value={value}>{children}</WaiverToastsContext.Provider>;
}

export default function useWaiverToasts(): WaiverToasts {
    const context = useContext(WaiverToastsContext);
    if (!context) {
        throw new Error('useWaiverToasts must be used within WaiverToastsProvider');
    }
    return context;
}
